/**
 * Admin Operations Dashboard Tab Component
 * 
 * Displays aggregated operations metrics and statistics.
 */

import { useQuery } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { getOperationsDashboard, type OperationsDashboardResponse } from "@app/services/operationsApi";
import "./admin-operations.css";

const OPERATIONS_QUERY_KEY = ["admin", "operations", "dashboard"];

export function AdminOperationsTab(): JSX.Element {
  const { data, isLoading, isError } = useQuery<OperationsDashboardResponse>({
    queryKey: OPERATIONS_QUERY_KEY,
    queryFn: getOperationsDashboard,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  if (isLoading) {
    return (
      <div className="admin-operations-root">
        <Card title="Operations Dashboard">
          <div className="admin-operations-loading">Loading operations data...</div>
        </Card>
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="admin-operations-root">
        <Card title="Operations Dashboard">
          <div className="admin-operations-error">Failed to load operations data.</div>
        </Card>
      </div>
    );
  }

  // Provide default values for nested objects to prevent undefined errors
  const executionsToday = data.executions_today || { total: 0, success: 0, failed: 0, pending: 0 };
  const payments = data.payments || { pending: 0, rejected: 0 };
  const subscriptions = data.subscriptions || { expiring_soon: 0 };
  const topErrors = data.top_errors || [];
  const timestamp = data.timestamp || new Date().toISOString();

  return (
    <div className="admin-operations-root">
      <Card
        title="Operations Dashboard"
        subtitle={`Last updated: ${new Date(timestamp).toLocaleString()}`}
      >
        <div className="admin-operations-grid">
          <div className="admin-operations-card">
            <h3>Executions Today</h3>
            <div className="admin-operations-metrics">
              <div className="admin-operations-metric">
                <span className="admin-operations-metric__label">Total</span>
                <span className="admin-operations-metric__value">{executionsToday.total}</span>
              </div>
              <div className="admin-operations-metric admin-operations-metric--success">
                <span className="admin-operations-metric__label">Success</span>
                <span className="admin-operations-metric__value">
                  {executionsToday.success}
                </span>
              </div>
              <div className="admin-operations-metric admin-operations-metric--failed">
                <span className="admin-operations-metric__label">Failed</span>
                <span className="admin-operations-metric__value">{executionsToday.failed}</span>
              </div>
              <div className="admin-operations-metric admin-operations-metric--pending">
                <span className="admin-operations-metric__label">Pending</span>
                <span className="admin-operations-metric__value">
                  {executionsToday.pending}
                </span>
              </div>
            </div>
          </div>

          <div className="admin-operations-card">
            <h3>Payments</h3>
            <div className="admin-operations-metrics">
              <div className="admin-operations-metric admin-operations-metric--warning">
                <span className="admin-operations-metric__label">Pending</span>
                <span className="admin-operations-metric__value">{payments.pending}</span>
              </div>
              <div className="admin-operations-metric admin-operations-metric--failed">
                <span className="admin-operations-metric__label">Rejected</span>
                <span className="admin-operations-metric__value">{payments.rejected}</span>
              </div>
            </div>
          </div>

          <div className="admin-operations-card">
            <h3>Subscriptions</h3>
            <div className="admin-operations-metrics">
              <div className="admin-operations-metric admin-operations-metric--warning">
                <span className="admin-operations-metric__label">Expiring Soon</span>
                <span className="admin-operations-metric__value">
                  {subscriptions.expiring_soon}
                </span>
                <span className="admin-operations-metric__note">(within 7 days)</span>
              </div>
            </div>
          </div>
        </div>

        {topErrors.length > 0 && (
          <div className="admin-operations-errors">
            <h3>Top Errors Today</h3>
            <div className="admin-operations-errors-list">
              {topErrors.map((error, index) => (
                <div key={index} className="admin-operations-error-item">
                  <div className="admin-operations-error-item__count">{error.count}</div>
                  <div className="admin-operations-error-item__message">
                    {error.error || "Unknown error"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

